import React, { useEffect, useRef, useState } from "react";
import { useScrollProgress } from "../lib/useInView";
import Reveal from "./Reveal";

/**
 * ParallaxInterlude — full-bleed cinematic image OR video section between
 * content sections. Image parallaxes + zooms; video plays muted/looped.
 * Pass `video` (mp4 url) for video mode; otherwise pass `image`.
 * Video src is only attached once the section enters the viewport
 * (lazy-mount) to avoid loading every interlude on initial paint.
 */
export default function ParallaxInterlude({
  image,
  video,
  poster,
  eyebrow,
  title,
  body,
  align = "left",
  height = "min-h-[85vh]",
  testid,
}) {
  const { ref, progress } = useScrollProgress();
  const videoElRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState(null);

  useEffect(() => {
    if (!video || !ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVideoSrc(video);
          obs.disconnect();
        }
      },
      { rootMargin: "200px 0px" }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [video, ref]);

  const translate = (progress - 0.5) * -16;
  const scale = 1.12 + progress * 0.04;

  return (
    <section
      ref={ref}
      data-testid={testid}
      className={`relative w-full ${height} overflow-hidden flex items-center bg-black text-white`}
    >
      <div
        className="absolute inset-0"
        style={{
          transform: `translate3d(0, ${translate}%, 0) scale(${scale.toFixed(3)})`,
          willChange: "transform",
        }}
        aria-hidden
      >
        {video ? (
          <video
            ref={videoElRef}
            className="w-full h-full object-cover"
            src={videoSrc || undefined}
            poster={poster}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden
          />
        ) : (
          <img
            src={image}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}
      </div>

      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 30%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.95) 100%)",
        }}
        aria-hidden
      />
      <div
        className="absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(45% 55% at 80% 50%, rgba(0,82,255,0.22), transparent 70%)",
        }}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className={`max-w-3xl ${align === "right" ? "ml-auto text-right" : ""}`}>
          {eyebrow && (
            <Reveal variant="up" delay={0}>
              <p className="text-xs tracking-[0.24em] uppercase font-semibold text-blue-300 mb-6">
                <span
                  className={`inline-block w-7 h-px bg-blue-300 align-middle ${
                    align === "right" ? "ml-3" : "mr-3"
                  }`}
                />
                {eyebrow}
              </p>
            </Reveal>
          )}
          <Reveal variant="up" delay={120}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-tighter text-balance leading-[1.02]">
              {title}
            </h2>
          </Reveal>
          {body && (
            <Reveal variant="up" delay={240}>
              <p className="mt-7 text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed">
                {body}
              </p>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
